/* eslint-disable react/require-default-props */
import React from 'react';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../utils/localization';

import './RoleImpact.css';

export type ImpactLevel = 'low' | 'medium' | 'high';

interface RoleImpactProps {
  impactLevel: ImpactLevel;
  showImpactText?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Helper function to get the appropriate icon kind for impact level
 */
const getImpactIconKind = (level: ImpactLevel): IconKind => {
  switch (level) {
    case 'high':
      return 'TrendUp';
    case 'medium':
      return 'TrendNeutral';
    case 'low':
      return 'TrendDown';
    default:
      return 'TrendUp';
  }
};

const invertedMessages = {
  'Low Impact': 'intelRecs.shared.impact.low',
  'Medium Impact': 'intelRecs.shared.impact.medium',
  'High Impact': 'intelRecs.shared.impact.high',
} as const;

type ImpactMessage = keyof typeof invertedMessages;

/**
 * Helper function to format impact text with proper capitalization
 * For known levels, returns a localization key; for unknown levels, preserves original behavior
 */
const formatImpactText = (level: ImpactLevel): string =>
  `${level.charAt(0).toUpperCase() + level.slice(1)} Impact`;

const getImpactMessageKey = (level: ImpactLevel): ImpactMessage | null => {
  switch (level) {
    case 'low':
      return 'Low Impact';
    case 'medium':
      return 'Medium Impact';
    case 'high':
      return 'High Impact';
    default:
      return null;
  }
};

/**
 * RoleImpact component for displaying role impact level with icon and optional text
 *
 * @param props - The component props
 * @returns JSX element containing the role impact indicator
 */
function RoleImpact({
  impactLevel,
  showImpactText = true,
  iconSize = 'small',
  className,
}: RoleImpactProps) {
  const l = useInvertedLocalize(invertedMessages);
  const iconKind = getImpactIconKind(impactLevel);
  const messageKey = getImpactMessageKey(impactLevel);
  const impactText = messageKey ? l(messageKey) : formatImpactText(impactLevel);

  return (
    <Box
      className={cx(
        'Roles-RoleImpact',
        `Roles-RoleImpact--${impactLevel}`,
        className
      )}
      role='img'
      aria-label={showImpactText ? undefined : impactText}>
      <Icon
        kind={iconKind}
        size={iconSize}
        className={cx(
          'Roles-RoleImpact-icon',
          `Roles-RoleImpact-icon--${impactLevel}`
        )}
      />
      {showImpactText && (
        <Typography
          kind='body3'
          className={cx(
            'Roles-RoleImpact-text',
            `Roles-RoleImpact-text--${impactLevel}`
          )}>
          {impactText}
        </Typography>
      )}
    </Box>
  );
}

export default RoleImpact;
